import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import MessageList from './MessageList';
import { campaignService } from '../../services/campaignService';
import { formatUTCToLocalDisplay, utcIsoToDate } from '../../utils/timezoneUtils';

const CampaignDetails = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCampaignDetails();
    fetchCampaignMessages();
  }, [campaignId]);

  const fetchCampaignDetails = async () => {
    try {
      const data = await campaignService.getCampaigns(null); // Get all campaigns regardless of year
      const campaignData = data.find(c => c.campaignId === campaignId);
      if (campaignData) {
        setCampaign(campaignData);
      } else {
        setError('Campaign not found');
      }
    } catch (err) {
      setError('Failed to fetch campaign details');
      console.error('Error fetching campaign:', err);
    }
  };

  const fetchCampaignMessages = async () => {
    try {
      const data = await campaignService.getCampaignMessages(campaignId, 'ALL');
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCampaignStatus = (campaign) => {
    if (!campaign) return 'Unknown';
    
    const now = new Date();
    const startDate = utcIsoToDate(campaign.scheduledTime);
    const endDate = campaign.endTime ? utcIsoToDate(campaign.endTime) : null;

    if (endDate && now > endDate) {
      return 'Completed';
    } else if (now >= startDate && (!endDate || now <= endDate)) {
      return 'Live';
    } else if (now < startDate) {
      return 'Scheduled';
    }
    return 'Unknown';
  };

  const canAddMessages = (campaign) => {
    if (!campaign) return false;
    
    const now = new Date();
    const endDate = campaign.endTime ? utcIsoToDate(campaign.endTime) : null;
    
    // Only hide the button if campaign has ended (has an end date and current time is past it)
    return !endDate || now <= endDate;
  };

  const canEditCampaign = (campaign) => {
    if (!campaign) return false;
    
    const now = new Date();
    const startDate = utcIsoToDate(campaign.scheduledTime);
    return now < startDate;
  };

  const formatDate = (dateString) => {
    return formatUTCToLocalDisplay(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#6B7280';
      case 'live':
      case 'active':
        return '#10B981';
      case 'scheduled':
        return '#3B82F6';
      case 'paused':
        return '#F59E0B';
      case 'cancelled':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 text-lg">Loading campaign details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500 text-lg">Error: {error}</div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500 text-lg">Campaign not found</div>
      </div>
    );
  }

  const status = getCampaignStatus(campaign);
  const statusColor = getStatusColor(status);
  const canAdd = canAddMessages(campaign);
  const canEdit = canEditCampaign(campaign);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link 
          to="/campaigns" 
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Campaigns
        </Link>
        
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{campaign.campaignName}</h1>
                <div className="mt-2 flex items-center space-x-4">
                  <span 
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: statusColor }}
                  >
                    {status}
                  </span>
                  <span className="text-sm text-gray-500">{campaign.campaignType}</span>
                </div>
              </div>

              <div className="flex space-x-3">
                {canAdd && (
                  <Link 
                    to={`/campaigns/${campaignId}/messages/new`} 
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    + New Message
                  </Link>
                )}
                
                {canEdit && (
                  <Link 
                    to={`/campaigns/${campaignId}/edit`} 
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Edit Campaign
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Start Time</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.scheduledTime)}</dd>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">End Time</dt>
                <dd className="mt-1 text-sm text-gray-900">{campaign.endTime ? formatDate(campaign.endTime) : 'Not set'}</dd>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.createdAt)}</dd>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(campaign.updatedAt)}</dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Campaign Messages</h2>
        </div>
        <div className="p-6">
          <MessageList 
            messages={messages} 
            campaignId={campaignId}
            canAddMessages={canAdd}
            onMessageUpdate={fetchCampaignMessages}
            campaign={campaign}
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignDetails;
